/**
 * Client-side localisation of in-app notifications.
 *
 * The backend stores notification `title` / `body` as plain strings (mostly
 * French). Rather than migrating stored documents, we recognise the known
 * server templates here and re-render them through i18n. Anything that does
 * not match a known template is displayed exactly as stored, so old, new and
 * unknown notifications all keep working.
 *
 * Priority: server-provided `title_key` / `body_key` → known template → raw text.
 */

const ANY = '([\\s\\S]+)';
const ID = '(\\S+)';

// [regex source, i18n key, param names in capture-group order, notification type]
const TITLE_RULES = [
  ['Nouvelle réservation', 'bookingCreatedProvider', [], 'booking_created'],
  ['Réservation enregistrée', 'bookingCreatedClient', [], 'booking_created'],
  ['Réservation confirmée ✅', 'bookingConfirmed', [], 'booking_confirmed'],
  ['Demande acceptée ✅', 'bookingAcceptedDeposit', [], 'booking_confirmed'],
  ['Réservation refusée', 'bookingRejected', [], 'booking_rejected'],
  ['Réservation clôturée en no-show', 'noShow', [], 'booking_rejected'],
  ['Acompte déclaré payé', 'depositDeclared', [], 'deposit_declared'],
  ['Acompte confirmé ✅', 'depositConfirmed', [], 'deposit_confirmed'],
  ['Vérification du paiement nécessaire', 'depositReview', [], 'deposit_review'],
  ['Réservation annulée par le voyageur', 'cancelledByTraveler', [], 'booking_cancelled'],
  ['Réservation annulée par le prestataire', 'cancelledByProvider', [], 'booking_cancelled'],
  ['Annonce validée', 'serviceApproved', [], 'service_approved'],
  ['Annonce refusée', 'serviceRejected', [], 'service_rejected'],
  ['Corrections demandées', 'serviceCorrections', [], 'service_corrections_needed'],
  ['Annonce suspendue', 'serviceSuspended', [], 'service_suspended'],
  ['Annonce en attente', 'servicePending', [], 'service_pending'],
  ["Offre supprimée par l'administration", 'serviceDeletedByAdmin', [], 'service_deleted_by_admin'],
  ["Réservation annulée par l'administration", 'bookingCancelledByAdmin', [], 'booking_cancelled_by_admin'],
  [`Nouveau message de ${ANY}`, 'newMessage', ['name'], 'new_message'],
  ['Nouvel avis (\\d+)/5', 'newReview', ['rating'], 'new_review'],
  ['Le prestataire vous a laissé un avis \\((\\d+)/5\\)', 'providerReviewedYou', ['rating'], 'new_review'],
  ['Réponse à votre avis', 'reviewReply', [], 'new_review'],
  ['⏰ Réservation en attente', 'reminderProvider', [], 'booking_reminder'],
  ['Votre réservation est toujours en attente', 'reminderClient', [], 'booking_reminder'],
  ['⌛ Réservation expirée', 'bookingExpired', [], 'booking_expired'],
  ['🟢 Règlement confirmé par Ridalo', 'paymentConfirmed', [], 'payment_confirmed'],
  ['🔵 SETTLEMENT SCHEDULED FOR TOMORROW', 'settlementTomorrow', [], 'settlement_reminder_sunday'],
  ['🟠 SETTLEMENT DUE TODAY', 'settlementDueToday', [], 'settlement_due_monday'],
  ['🔴 SETTLEMENT OVERDUE', 'settlementOverdue', [], 'settlement_overdue'],
];

const SERVICE_STATUS_FR = {
  'validée': 'approved',
  'refusée': 'rejected',
  'à corriger': 'corrections_needed',
  'suspendue': 'suspended',
  'en attente': 'pending',
};

const BODY_RULES = [
  [`${ANY} a réservé ${ANY}`, 'bookingCreatedProvider', ['name', 'title'], 'booking_created'],
  [`Votre demande ${ID} est en attente de confirmation`, 'bookingCreatedClient', ['id'], 'booking_created'],
  [`Le prestataire a confirmé ${ID}`, 'bookingConfirmed', ['id'], 'booking_confirmed'],
  [`Votre demande ${ID} a été acceptée\\. Un acompte de ${ANY} THB est nécessaire : consultez votre réservation pour les instructions de paiement\\.`, 'bookingAcceptedDeposit', ['id', 'amount'], 'booking_confirmed'],
  [`Le prestataire n'a pas pu accepter ${ID}`, 'bookingRejected', ['id'], 'booking_rejected'],
  [`Le prestataire a signalé votre absence à la prise en charge pour ${ID}\\. L'acompte de ${ANY} THB a été retenu\\.`, 'noShowRetained', ['id', 'amount'], 'booking_rejected'],
  [`Le prestataire a signalé votre absence à la prise en charge pour ${ID}\\. Aucun acompte n'était retenu\\.`, 'noShowNone', ['id'], 'booking_rejected'],
  [`Le voyageur a déclaré avoir payé l'acompte pour ${ID}\\. Merci de confirmer\\.`, 'depositDeclared', ['id'], 'deposit_declared'],
  [`Le voyageur a déclaré avoir payé son acompte de ${ANY} THB\\. Veuillez vérifier la réception des fonds avant le ${ANY} \\(heure de Thaïlande\\)\\.`, 'depositDeclaredDeadline', ['amount', 'date'], 'deposit_declared'],
  [`Le prestataire n'a pas encore confirmé la réception de votre acompte pour ${ID}\\. Votre réservation n'est pas annulée : l'équipe Ridalo va vérifier la situation\\.`, 'depositReviewClient', ['id'], 'deposit_review'],
  [`Le délai de vérification de l'acompte déclaré pour ${ID} est dépassé\\. Si les fonds sont arrivés, confirmez la réception ; sinon, contactez l'équipe Ridalo\\.`, 'depositReviewProvider', ['id'], 'deposit_review'],
  [`Le prestataire a confirmé la réception de l'acompte \\(${ANY} THB\\) pour ${ID}\\.`, 'depositConfirmed', ['amount', 'id'], 'deposit_confirmed'],
  [`${ID} — Motif : ${ANY}`, 'cancelledWithReason', ['id', 'reason'], 'booking_cancelled'],
  [`Votre offre « ${ANY} » a été supprimée par l'équipe Ridalo\\.`, 'serviceDeletedByAdmin', ['title'], 'service_deleted_by_admin'],
  [`La réservation « ${ANY} » a été annulée par l'équipe Ridalo\\.`, 'bookingCancelledByAdmin', ['title'], 'booking_cancelled_by_admin'],
  [`${ID} attend une réponse depuis plus de (\\d+)h`, 'reminderProvider', ['id', 'hours'], 'booking_reminder'],
  [`Nous avons relancé le prestataire pour ${ID}`, 'reminderClient', ['id'], 'booking_reminder'],
  [`Votre règlement de ${ANY} ฿ a été confirmé et payé\\.`, 'paymentConfirmed', ['amount'], 'payment_confirmed'],
  [`Your next settlement is scheduled for Monday at 12:00\\. Net to receive: ${ANY} ฿`, 'settlementTomorrow', ['amount'], 'settlement_reminder_sunday'],
  [`Your settlement is due today by 12:00\\. Net to receive: ${ANY} ฿`, 'settlementDueToday', ['amount'], 'settlement_due_monday'],
  [`The settlement scheduled for today at 12:00 has not yet been confirmed\\. Net amount: ${ANY} ฿`, 'settlementOverdue', ['amount'], 'settlement_overdue'],
].map(([src, key, names, type]) => [new RegExp(`^${src}$`), key, names, type]);

const COMPILED_TITLES = TITLE_RULES.map(([src, key, names, type]) => [new RegExp(`^${src}$`), key, names, type]);

const SERVICE_BODY = new RegExp(`^${ANY} — (validée|refusée|à corriger|suspendue|en attente)(?: · Note admin : ${ANY})?$`);

const matchRules = (n, text, rules, prefix, t) => {
  for (const [re, key, names, type] of rules) {
    if (n.type !== type) continue;
    const m = re.exec(text);
    if (m) {
      const params = {};
      names.forEach((name, i) => { params[name] = m[i + 1]; });
      return t(`${prefix}.${key}`, params);
    }
  }
  return null;
};

const localizeServiceBody = (n, t) => {
  if (typeof n.type !== 'string' || !n.type.startsWith('service_')) return null;
  const m = SERVICE_BODY.exec(n.body);
  if (!m) return null;
  const status = t(`notifTemplates.serviceStatus.${SERVICE_STATUS_FR[m[2]]}`);
  return m[3]
    ? t('notifTemplates.body.serviceStatusWithNote', { title: m[1], status, note: m[3] })
    : t('notifTemplates.body.serviceStatus', { title: m[1], status });
};

export const localizeNotification = (n, t) => {
  let title = n.title;
  let body = n.body;
  if (n.title_key) {
    title = t(n.title_key, n.params || {});
  } else if (typeof n.title === 'string') {
    title = matchRules(n, n.title, COMPILED_TITLES, 'notifTemplates.title', t) ?? n.title;
  }
  if (n.body_key) {
    body = t(n.body_key, n.params || {});
  } else if (typeof n.body === 'string' && n.body) {
    body = localizeServiceBody(n, t)
      ?? matchRules(n, n.body, BODY_RULES, 'notifTemplates.body', t)
      ?? n.body;
  }
  return { title, body };
};
